import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { X, MessageSquare, ExternalLink, Smartphone, Copy, Check } from 'lucide-react';
import { safeFetch } from '../../utils/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Detect mobile device (covers iOS, Android, Windows Phone). Tablets included.
const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

/**
 * Native SMS handoff modal.
 * On mobile: primary action opens the device's native Messages app via `sms:` URI.
 * On desktop: primary action copies the rendered message to clipboard (since `sms:`
 * is unreliable on desktop), with "Open in SMS App" demoted to secondary.
 *
 * Props:
 *  - booking: booking object (for variable substitution and recipient)
 *  - defaultTo: phone number to send to
 *  - onClose: () => void
 */
const SendSMSModal = ({ booking, defaultTo, onClose }) => {
  const [to, setTo] = useState(defaultTo || '');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [message, setMessage] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [copied, setCopied] = useState(false);

  const isMobile = useMemo(() => isMobileDevice(), []);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('admin_token');
      const res = await safeFetch(`${API_URL}/api/admin/sms-templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setTemplates(res.data.templates || []);
      setLoadingTemplates(false);
    })();
  }, []);

  const handleTemplateChange = async (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) {
      setMessage('');
      return;
    }
    const token = localStorage.getItem('admin_token');
    const res = await safeFetch(`${API_URL}/api/admin/sms-templates/${templateId}/preview`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ booking_id: booking?.id || null }),
    });
    if (res.ok) {
      setMessage(res.data.rendered || '');
    }
  };

  // Build sms: URI compatible with iOS + Android.
  // Format: sms:NUMBER?body=URLENCODED_TEXT (works on modern iOS and Android).
  const buildSmsHref = () => {
    const number = (to || '').replace(/\s+/g, '');
    const body = encodeURIComponent(message || '');
    if (!number) return '#';
    return `sms:${number}?body=${body}`;
  };

  const canOpen = !!to.trim() && !!message.trim();
  const chars = message.length;
  const segments = chars === 0 ? 0 : Math.ceil(chars / 160);

  const handleCopy = async () => {
    if (!message.trim()) return;
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = message;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch (_) { /* noop */ }
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid="send-sms-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#E2E8F0]">
          <h2 className="text-lg font-semibold text-[#111111] flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#0D9488]" /> Send SMS
          </h2>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#111111]" data-testid="close-sms-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {isMobile ? (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-800 flex items-start gap-2">
              <Smartphone className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                This opens your phone's native <strong>Messages</strong> app with the message pre-filled.
                You'll send it from your own number — no Twilio, no charges from Transa.
              </span>
            </div>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 flex items-start gap-2">
              <Copy className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                You're on a desktop. Click <strong>Copy Message</strong>, then paste it into Messages on your phone to send.
                (Opening directly only works reliably on mobile.)
              </span>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5 uppercase tracking-wide">To</label>
            <Input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="04xx xxx xxx"
              data-testid="sms-to-input"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-[#64748B] uppercase tracking-wide">Template</label>
              <a
                href="/admin/sms-templates"
                target="_blank"
                rel="noreferrer"
                className="text-[11px] text-[#0D9488] hover:underline flex items-center gap-1"
                data-testid="manage-templates-link"
              >
                Manage <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <select
              value={selectedTemplateId}
              onChange={(e) => handleTemplateChange(e.target.value)}
              disabled={loadingTemplates}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="sms-template-select"
            >
              <option value="">-- Freeform message --</option>
              {templates.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5 uppercase tracking-wide">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              placeholder="Type your SMS or select a template above"
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="sms-message-input"
            />
            <p className="text-xs text-[#64748B] mt-1">{chars} chars · {segments} segment{segments === 1 ? '' : 's'}</p>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-[#E2E8F0] bg-[#F8FAFC]">
          <Button variant="outline" onClick={onClose} data-testid="cancel-sms-btn">Cancel</Button>

          {isMobile ? (
            <>
              {/* Mobile: Copy is secondary, Open is primary */}
              <Button
                type="button"
                variant="outline"
                onClick={handleCopy}
                disabled={!message.trim()}
                data-testid="copy-sms-btn"
              >
                {copied ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Copy</>}
              </Button>
              <a
                href={canOpen ? buildSmsHref() : undefined}
                onClick={(e) => {
                  if (!canOpen) {
                    e.preventDefault();
                    return;
                  }
                  setTimeout(() => onClose(), 300);
                }}
                className={`inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium text-white ${canOpen ? 'bg-[#0D9488] hover:bg-[#0F766E] cursor-pointer' : 'bg-[#94A3B8] cursor-not-allowed pointer-events-none'}`}
                data-testid="open-native-sms-btn"
              >
                <Smartphone className="w-4 h-4 mr-1" /> Open in SMS App
              </a>
            </>
          ) : (
            <>
              {/* Desktop: Copy is primary, Open is secondary fallback */}
              <a
                href={canOpen ? buildSmsHref() : undefined}
                onClick={(e) => {
                  if (!canOpen) e.preventDefault();
                }}
                className={`inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium ${canOpen ? 'text-[#111111] hover:bg-[#F1F5F9] cursor-pointer' : 'text-[#94A3B8] cursor-not-allowed pointer-events-none'}`}
                data-testid="open-native-sms-btn"
                title="May not work on desktop without a paired phone"
              >
                <Smartphone className="w-4 h-4 mr-1" /> Try SMS App
              </a>
              <Button
                type="button"
                onClick={handleCopy}
                disabled={!message.trim()}
                className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
                data-testid="copy-sms-btn"
              >
                {copied ? <><Check className="w-4 h-4 mr-1" /> Copied!</> : <><Copy className="w-4 h-4 mr-1" /> Copy Message</>}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SendSMSModal;
